import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  EntityState,
  PayloadAction,
} from "@reduxjs/toolkit"

import { DataStatus, Driver, RootState, Subteam, Team } from "@/types"
import { fetchDrivers, fetchUpdateDriverSubteams } from "@/utils"
import { handleStandardError } from "@/utils/errors"

import { setDate } from "./global"
import { setCurrentTeam } from "./teams"
import { clearDataOnLogout } from "./user"

const driversAdapter = createEntityAdapter<Driver>({ sortComparer: false })

type DriversState = EntityState<Driver> & {
  status: DataStatus | "updating" | null
}

const initialState: DriversState = driversAdapter.getInitialState({
  status: "no_team",
})

export const getDrivers = createAsyncThunk(
  "drivers/getDrivers",
  async ({ teamIds, date }: { teamIds: Array<Team["id"]>; date: string }) => {
    const { response, json } = await fetchDrivers(teamIds, date)
    return {
      ok: response.ok,
      status: response.status,
      data: json.data,
    }
  }
)

export const updateDriverSubteams = createAsyncThunk(
  "driver/updateDriverSubteams",
  async ({ driverId, sub_team_ids }: { driverId: Driver["id"]; sub_team_ids: Array<Subteam["id"]> }, { dispatch }) => {
    const { response, json } = await fetchUpdateDriverSubteams(driverId, sub_team_ids)

    const { data: driver } = json

    if (!response.ok || !driver) {
      handleStandardError(response, json)

      return {
        ok: response.ok,
        status: response.status,
      }
    }

    dispatch(updateDriver(driver))

    return {
      ok: response.ok,
      status: response.status,
      data: driver,
    }
  }
)

export const updateDrivers = createAction<Driver[]>("drivers/updateDrivers")

export const driversReducer = (state = initialState, action: PayloadAction<Driver | Driver[]>) => {
  if (updateDrivers.match(action)) {
    if (state.status !== "fetched") return state

    const driverUpdates = action.payload
    const updatedEntities = { ...state.entities }

    driverUpdates.forEach(
      (driverUpdate) =>
        (updatedEntities[driverUpdate.id] = Object.assign({}, updatedEntities[driverUpdate.id], driverUpdate))
    )

    return { ...state, entities: updatedEntities }
  }

  return driversSlice.reducer(state, action)
}

export const clearGlobalDrivers = createAsyncThunk("drivers/clearGlobalDrivers", (_, { dispatch, getState }) => {
  const {
    teams: { current: currentTeam },
    drivers: { entities: driversEntities },
  } = getState() as RootState

  dispatch(
    removeDrivers(
      Object.values(driversEntities)
        .filter((driverEntity) => driverEntity?.team_id !== currentTeam?.id)
        .map((entity) => entity?.id as number)
    )
  )
})

const driversSlice = createSlice({
  name: "drivers",
  initialState,
  reducers: {
    updateDriver(state, action: PayloadAction<Driver>) {
      const updatedDriver = action.payload

      driversAdapter.upsertOne(state, updatedDriver)
    },
    removeDrivers(state, action: PayloadAction<Array<Driver["id"]>>) {
      driversAdapter.removeMany(state, action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getDrivers.pending, (state) => {
      if (state.ids.length) {
        state.status = "updating"
      } else {
        state.status = "loading"
      }
    })

    builder.addCase(getDrivers.fulfilled, (state, { payload }) => {
      if (payload.ok) {
        if (payload.data) driversAdapter.upsertMany(state, payload.data)
        state.status = "fetched"
      } else {
        state.status = "error"
      }
    })

    builder.addCase(getDrivers.rejected, (state, action) => {
      if (state.status === "updating" && action.meta.aborted) state.status = "fetched"
    })

    builder.addCase(setCurrentTeam, (state) => {
      driversAdapter.removeAll(state)
    })
    builder.addCase(setDate, (state) => {
      driversAdapter.removeAll(state)
    })
    builder.addCase(clearDataOnLogout, () => initialState)
  },
})

const { selectById, selectIds, selectAll, selectEntities } = driversAdapter.getSelectors(
  (state: RootState) => state.drivers
)

export const selectDriversIds = selectIds

export const selectDrivers = selectAll

export const selectDriversEntities = selectEntities

export const selectDriverById = (id: Driver["id"]) => (state: RootState) => selectById(state, id)

export const selectFilteredDrivers = (...filters: Array<(driver: Driver) => boolean>) =>
  createSelector(selectDrivers, (drivers) => drivers.filter((driver) => filters.every((filter) => filter(driver))))

export const selectFilteredDriversIds = (...filters: Array<(driver: Driver) => boolean>) =>
  createSelector(selectFilteredDrivers(...filters), (drivers) => drivers.map((driver) => driver.id))

export const selectFilteredDriversCount = (...filters: Array<(driver: Driver) => boolean>) =>
  createSelector(selectFilteredDrivers(...filters), (drivers) => drivers.length)

export const selectActiveDriversIds = createSelector(selectDrivers, (drivers) =>
  drivers.filter((driver) => driver.state === "free" || driver.state === "busy").map((driver) => driver.id)
)

export const selectInactiveDriversIds = createSelector(selectDrivers, (drivers) =>
  drivers.filter((driver) => driver.state === "offline").map((driver) => driver.id)
)

export const selectDriversStatus = (state: RootState) => state.drivers.status

export const { updateDriver, removeDrivers } = driversSlice.actions

export const selectSortedFilteredDriversIds = (
  sorter: ((a: Driver, b: Driver) => number) | null,
  ...filters: Array<(driver: Driver) => boolean>
) =>
  createSelector(selectFilteredDrivers(...filters), (drivers) => {
    return sorter ? drivers.sort(sorter).map((driver) => driver.id) : drivers.map((driver) => driver.id)
  })
